<script lang="ts">
const controls: EditorControl[][] = [
  [
    {
      tag: 'copy',
      type: 'button',
      icon: CopySelectIcon,
      disableIfImageSelected: false,
      action: (editor, _, selectedImage) => {
        if (selectedImage.value) {
          copyImageToClip(selectedImage.value);
        } else {
          // same as normal copy
          const clipboardData = new DataTransfer();
          const event = new ClipboardEvent('copy', { clipboardData });
          editor.fireEvent('copy', event);
          const dataByType: Record<string, Blob> = {};
          for (const clipType of clipboardData.types) {
            dataByType[clipType] = new Blob([clipboardData.getData(clipType)], { type: clipType });
          }
          navigator.clipboard.write([new ClipboardItem(dataByType)]);
        }
      },
    },
    {
      tag: 'cut',
      type: 'button',
      icon: CutIcon,
      disableIfImageSelected: false,
      action: (editor, _, selectedImage) => {
        if (selectedImage.value) {
          copyImageToClip(selectedImage.value);
          selectedImage.value.remove();
          selectedImage.value = null;
        } else {
          // same as normal cut
          const clipboardData = new DataTransfer();
          const event = new ClipboardEvent('cut', { clipboardData });
          editor.fireEvent('cut', event);
          const dataByType: Record<string, Blob> = {};
          for (const clipType of clipboardData.types) {
            dataByType[clipType] = new Blob([clipboardData.getData(clipType)], { type: clipType });
          }
          navigator.clipboard.write([new ClipboardItem(dataByType)]);
        }
      },
    },
    {
      tag: 'paste',
      type: 'button',
      icon: ClipboardPasteIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        (async () => {
          const clipboardData = new DataTransfer();
          const items = await navigator.clipboard.read();
          for (let i = 0; i < items.length; i++) {
            const element = items[i];
            for (const elType of element.types) {
              const blob = await element.getType(elType);
              if (elType.startsWith('image')) {
                clipboardData.setData(blob.type, await blobToBase64(blob));
              } else {
                clipboardData.setData(blob.type, await blob.text());
              }
            }
          }
          const event = new ClipboardEvent('paste', { clipboardData });
          // same as normal paste
          editor.fireEvent('paste', event);
        })();
      },
    },
  ],
  [
    {
      tag: 'undo',
      type: 'button',
      icon: ArrowUndoIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.undo();
      },
    },
    {
      tag: 'redo',
      type: 'button',
      disableIfImageSelected: false,
      icon: ArrowRedoIcon,
      action: (editor) => {
        editor.redo();
      },
    },
  ],
  [
    {
      tag: 'clear_formatting',
      type: 'button',
      disableIfImageSelected: true,
      icon: TextClearFormattingIcon,
      action: (editor) => {
        editor.removeAllFormatting();
      },
    },
  ],
  [
    {
      tag: 'add_image',
      type: 'add-image',
      icon: ImageAddIcon,
      disableIfImageSelected: false,
      action: (files, editor) => {
        (async () => {
          for (const file of Array.from(files)) {
            const base64Image = await blobToBase64(file);
            editor.insertImage(base64Image, { style: 'max-width: 600px' });
          }
        })();
      },
    },
    {
      tag: 'add_link',
      type: 'add-link',
      icon: LinkIcon,
      disableIfImageSelected: false /* could also be enabled if we want to allow images that can link */,
      action: (value, editor) => {
        editor.makeLink(value, { target: '_blank' });
      },
    },
  ],
  [
    {
      tag: 'font_face',
      type: 'select',
      values: Array.from(
        new Set([
          'Arial',
          'Times New Roman',
          'Courier New',
          'Georgia',
          'Trebuchet MS',
          'Helvetica Neue',
          'Lucida Grande',
          'Segoe UI',
          FALLBACK_FONT,
        ])
      ).sort(),
      disableIfImageSelected: true,
      default: FALLBACK_FONT,
      getCurrent: (editor) =>
        editor.getFontInfo(editor.getSelection()).fontFamily?.split(',')[0].replaceAll('"', '') ??
        FALLBACK_FONT,
      action: (value, editor) => {
        editor.setFontFace(`${value}, ${FALLBACK_FONT}`);
      },
    },
    {
      tag: 'font_size',
      type: 'select',
      values: ['9', '10', '11', '12', '14', '16', '18', '24', '36', '48', '64'],
      disableIfImageSelected: true,
      default: '16',
      getCurrent: (editor) =>
        editor.getFontInfo(editor.getSelection()).fontSize?.slice(0, -'px'.length) ?? '16',
      action: (value, editor) => {
        editor.setFontSize(`${value}px`);
      },
    },
  ],
  [
    {
      tag: 'b',
      type: 'toggle',
      icon: TextBoldIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeBold();
        } else {
          editor.bold();
        }
      },
    },
    {
      tag: 'i',
      type: 'toggle',
      icon: TextItalicIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeItalic();
        } else {
          editor.italic();
        }
      },
    },
    {
      tag: 'u',
      type: 'toggle',
      icon: TextUnderlineIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeUnderline();
        } else {
          editor.underline();
        }
      },
    },
    {
      tag: 's',
      type: 'toggle',
      icon: TextStrikethroughIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeStrikethrough();
        } else {
          editor.strikethrough();
        }
      },
    },
  ],
  [
    {
      tag: 'sub',
      type: 'toggle',
      icon: TextSubscriptIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeSubscript();
        } else {
          editor.subscript();
        }
      },
    },
    {
      tag: 'sup',
      type: 'toggle',
      icon: TextSuperscriptIcon,
      disableIfImageSelected: true,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeSuperscript();
        } else {
          editor.superscript();
        }
      },
    },
  ],
  [
    {
      tag: 'line_spacing',
      type: 'line-spacing',
      icon: TextLineSpacingIcon,
      disableIfImageSelected: true,
      action: (lineHeight, editor) => {
        if (
          editor.hasFormat('span', {
            class: 'line-height',
          })
        ) {
          editor.changeFormat(null, {
            tag: 'span',
            attributes: { class: 'line-height' },
          });
        }
        editor.changeFormat({
          tag: 'span',
          attributes: {
            class: 'line-height',
            style: `line-height: ${lineHeight}`,
          },
        });
      },
    },
  ],
  [
    {
      tag: 'text_color',
      type: 'color-picker',
      icon: TextColorIcon,
      disableIfImageSelected: true,
      action: (color, editor) => {
        editor.setTextColor(color);
      },
    },
    {
      tag: 'highlight',
      type: 'color-picker',
      icon: HighlightIcon,
      disableIfImageSelected: true,
      action: (color, editor) => {
        editor.setHighlightColor(color);
      },
    },
  ],
  [
    {
      tag: 'align_left',
      type: 'toggle',
      icon: TextAlignLeftIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.setTextAlignment('left');
      },
    },
    {
      tag: 'align_center',
      type: 'toggle',
      icon: TextAlignCenterIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.setTextAlignment('center');
      },
    },
    {
      tag: 'align_right',
      type: 'toggle',
      icon: TextAlignRightIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.setTextAlignment('right');
      },
    },
  ],
  [
    {
      tag: 'ul',
      type: 'toggle',
      icon: TextBulletListIcon,
      disableIfImageSelected: false,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeList();
        } else {
          editor.makeUnorderedList();
        }
      },
    },
    {
      tag: 'ol',
      type: 'toggle',
      icon: TextNumberListLtrIcon,
      disableIfImageSelected: false,
      action: (editor, tag) => {
        if (editor.hasFormat(tag)) {
          editor.removeList();
        } else {
          editor.makeOrderedList();
        }
      },
    },
  ],
  [
    {
      tag: 'hr',
      type: 'button',
      icon: HorizontalRuleIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.insertHTML("<div><hr style='margin:10px 0'></div><div><br></div>");
      },
    },
  ],
  [
    {
      tag: 'increase_indent',
      type: 'button',
      icon: TextIndentIncreaseIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        changeIndent(editor, 'in');
      },
    },
    {
      tag: 'decrease_indent',
      type: 'button',
      icon: TextIndentDecreaseIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        changeIndent(editor, 'out');
      },
    },
  ],
  [
    {
      tag: 'add_quote',
      type: 'button',
      icon: TextQuoteIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.increaseQuoteLevel();
      },
    },
    {
      tag: 'remove_quote',
      type: 'button',
      icon: TextQuoteOpeningIcon,
      disableIfImageSelected: false,
      action: (editor) => {
        editor.decreaseQuoteLevel();
      },
    },
  ],
  [
    {
      tag: 'add_table',
      type: 'add-table',
      icon: TableAddIcon,
      disableIfImageSelected: false,
      action: (cols, rows, editor) => {
        let html = '<table style="border-collapse: collapse; width: auto; margin: 8px 0;">';
        for (let r = 0; r < rows; r++) {
          html += '<tr>';
          for (let c = 0; c < cols; c++) {
            html +=
              '<td style="border: 1px solid var(--nxt-table-border); padding: 6px 10px; min-width: 40px; vertical-align: top;"><br></td>';
          }
          html += '</tr>';
        }
        html += '</table>';
        editor.insertHTML(html);
        return (editor.getSelection().commonAncestorContainer as Element).closest(
          'table'
        ) as HTMLTableElement;
      },
    },
  ],
];
</script>

<script setup lang="ts">
import {
  ArrowRedoIcon,
  ArrowUndoIcon,
  ClipboardPasteIcon,
  CopySelectIcon,
  CutIcon,
  HighlightIcon,
  HorizontalRuleIcon,
  ImageAddIcon,
  LinkIcon,
  TableAddIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  TextBoldIcon,
  TextBulletListIcon,
  TextClearFormattingIcon,
  TextColorIcon,
  TextIndentDecreaseIcon,
  TextIndentIncreaseIcon,
  TextItalicIcon,
  TextLineSpacingIcon,
  TextNumberListLtrIcon,
  TextQuoteIcon,
  TextQuoteOpeningIcon,
  TextStrikethroughIcon,
  TextSubscriptIcon,
  TextSuperscriptIcon,
  TextUnderlineIcon,
} from '@blpde/nx-icons';
import DOMPurify from 'dompurify';
import Squire from 'squire-rte';
import { inject, onMounted, onUnmounted, type Ref, ref, useId, useTemplateRef, watch } from 'vue';
import { NxButton } from './../Button';
import { NxSelectInput } from './../Inputs';
import { NxTooltip } from './../Tooltip';
import AddLink from './AddLink.vue';
import AddTable from './AddTable.vue';
import ChangeLineSpacing from './ChangeLineSpacing.vue';
import ColorPicker from './ColorPicker.vue';
import {
  after,
  blobToBase64,
  changeIndent,
  copyImageToClip,
  deleteCol,
  deleteRow,
  EditorControl,
  FALLBACK_FONT,
  insertCol,
  insertRow,
} from './internal';
import { i18nInjectionKey } from './../internal';

const props = withDefaults(
  defineProps<{
    label?: string;
    disabled?: boolean;
    showActionBar?: boolean;
    focusOnLoad?: boolean;
    internalScroll?: boolean;
    /**
     * Declared so the field's full editor can set it on any surface (BLP/nx#3328); this one
     * has no use for it: its Tab already inserts an em space.
     */
    insertTabCharacter?: boolean;
  }>(),
  {
    showActionBar: true,
    focusOnLoad: true,
    disabled: false,
    internalScroll: true,
    insertTabCharacter: false,
  }
);

const editorRef = useTemplateRef<HTMLElement>('editor-ref');
const editor = ref<Squire>();
const uniqueId = useId();
const anchorName = `--editor-${useId()}`;

const selectedImage = ref<HTMLImageElement | null>(null);
const selectedTable = ref<HTMLTableElement | null>(null);
const selectedTableRowCount = ref<number>(0);
const selectedTableColCount = ref<number>(0);
const hideSelection = ref<boolean>(false);
const tableAnchorCSS = ref('');
const currentValueByTag = ref(
  new Map(controls.flatMap((group) => group.map((c) => [c.tag, c.type === 'select' ? '' : false])))
);

const i18nInjection = inject(i18nInjectionKey)!;

/**
 * Maps a control `tag` (a functional command id, kept as-is) to its i18n suffix
 * under `common.editor.*`. Tags whose name matches the i18n key pass through.
 */
const EDITOR_I18N_TAG: Record<string, string> = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
  s: 'strikethrough',
  sub: 'subscript',
  sup: 'superscript',
  ul: 'unordered_list',
  ol: 'ordered_list',
  hr: 'horizontal_rule',
};
function editorI18nTag(tag: string): string {
  return EDITOR_I18N_TAG[tag] ?? tag;
}

function updateTableHandle(table: HTMLTableElement | null) {
  selectedTableRowCount.value = table ? table.querySelectorAll('tr').length : 0;
  selectedTableColCount.value = table
    ? Array.from(table.querySelectorAll('tr')).reduce(
        (mx, tr) => Math.max(mx, tr.querySelectorAll('td').length),
        0
      )
    : 0;

  // Selection handles belong to editor chrome, never to saved HTML or undo states.
  // Anchor the current cells through a local stylesheet instead of mutating their style attributes.
  const rules: string[] = [];
  table?.querySelectorAll('tr').forEach((tr, y) => {
    tr.querySelectorAll('td').forEach((td, x) => {
      const path: string[] = [];
      let node: Element | null = td;
      while (node && node !== editorRef.value) {
        const parent: Element | null = node.parentElement;
        if (!parent) return;
        path.unshift(`:nth-child(${Array.from(parent.children).indexOf(node) + 1})`);
        node = parent;
      }
      if (node === editorRef.value)
        rules.push(
          `[data-nx-editor="${uniqueId}"] > ${path.join(' > ')} { anchor-name: --td-${x}-${y}-${uniqueId}; }`
        );
    });
  });
  tableAnchorCSS.value = rules.join('\n');
}
watch(selectedTable, (newVal) => {
  updateTableHandle(newVal);
});

function onKeydown(e: KeyboardEvent) {
  if (props.disabled) {
    return;
  }
  if (e.key === 'Escape') {
    return; // let ESC bubble up
  }
  e.stopPropagation();

  // image copy/cut
  if (selectedImage.value) {
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      copyImageToClip(selectedImage.value);
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
      e.preventDefault();
      copyImageToClip(selectedImage.value);
      editorRef.value?.querySelector('.squire-image-resize-container')?.remove();
      selectedImage.value?.remove();
      selectedImage.value = null;
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      editor.value?._imageResizer.handleEvent(e);
      selectedImage.value = null;
      return;
    }
    if (e.key.startsWith('Arrow')) {
      // Only ArrowUp and Down are implemented in Squire imageResizer so we use one of them
      editor.value?._imageResizer.handleEvent(new KeyboardEvent(e.type, { ...e, key: 'ArrowUp' }));
      // and adapt the selection/caret later
      const range = document.createRange();
      if (e.key === 'ArrowLeft') {
        range.setStartBefore(selectedImage.value);
        range.setEndBefore(selectedImage.value);
      } else {
        range.setStartAfter(selectedImage.value);
        range.setEndAfter(selectedImage.value);
      }
      editor.value!.setSelection(range);
      selectedImage.value = null;
      // to prevent the extra step
      e.preventDefault();
      return;
    }
  }

  // tab
  if (e.key === 'Tab') {
    e.preventDefault(); /* don't jump out of the editor */
    const selection = editor.value!.getSelection();
    if (
      selection.startOffset === 0 &&
      selection.endOffset === 0 &&
      selection.startContainer === selection.endContainer &&
      ((selection.startContainer as Element).tagName === 'LI' ||
        selection.startContainer.parentElement?.tagName === 'LI')
    ) {
      // indent the li -> done automatically by squire
      editor.value?.handleEvent(e);
      return;
    }

    // insert tab otherwise
    if (e.shiftKey) return; // special case shift-tab

    if (selectedImage.value) {
      editorRef.value?.querySelector('.squire-image-resize-container')?.remove();
      selectedImage.value?.remove();
      selectedImage.value = null;
    }
    editor.value!.insertHTML('&emsp;', false);
    return;
  }

  // anything else
  editor.value?.handleEvent(e);
  if (selectedImage.value && !editorRef.value!.contains(selectedImage.value)) {
    editorRef.value?.querySelector('.squire-image-resize-container')?.remove();
    selectedImage.value = null;
  }

  if (e.key.startsWith('Arrow')) {
    // setTimeout so we get the new selection
    setTimeout(() => {
      const sel = editor.value!.getSelection();
      const table = sel.startContainer.parentElement?.closest('table');
      if (table) {
        selectedTable.value = table;
      } else {
        selectedTable.value = null;
      }
    }, 1);
  }
}

function onClick(e: PointerEvent) {
  if (props.disabled) {
    return;
  }

  editor.value?._imageResizer.handleEvent(e);

  if ((e.target as Element).tagName === 'IMG') {
    selectedImage.value = e.target as HTMLImageElement;
    hideSelection.value = true;
    const range = document.createRange();
    range.setStartBefore(selectedImage.value);
    range.setEndAfter(selectedImage.value);
    editor.value!.setSelection(range);
    editor.value!.focus();
  } else {
    const resizeHandle = editorRef.value?.querySelector('.squire-image-resize-container');
    if (e.target !== resizeHandle && (!resizeHandle || !resizeHandle!.contains(e.target as Node))) {
      selectedImage.value = null;
    }
  }

  const table = (e.target as Element).closest('table');
  if (table) {
    selectedTable.value = table;
  } else {
    selectedTable.value = null;
  }
}

function callWithRef(fn: (asRef: Ref<HTMLImageElement | null>) => void) {
  fn(selectedImage);
}

onMounted(() => {
  editor.value = new Squire(editorRef.value!, {
    tagAttributes: {
      ul: {
        style: `padding-left: 30px`,
      },
      ol: {
        style: `padding-left: 30px`,
      },
      blockquote: {
        style:
          'padding-top: 10px; padding-bottom: 10px; padding-left: 16px; border-left: 5px solid var(--nxt-border);',
      },
      li: { style: 'font-weight: 400' },
    },
    // shouldn't be needed, but currently there is an issue: https://github.com/fastmail/Squire/issues/493
    sanitizeToDOMFragment: (
      html: string
      /* editor: Squire, */
    ): DocumentFragment => {
      const frag = DOMPurify.sanitize(html, {
        ALLOW_UNKNOWN_PROTOCOLS: true,
        WHOLE_DOCUMENT: false,
        RETURN_DOM: true,
        RETURN_DOM_FRAGMENT: true,
        FORCE_BODY: false,
      });
      return frag
        ? (document.importNode(frag, true) as DocumentFragment)
        : document.createDocumentFragment();
    },
  });

  function updateCurrentValues() {
    for (const group of controls) {
      for (const control of group) {
        currentValueByTag.value.set(
          control.tag,
          control.type === 'select'
            ? control.getCurrent(editor.value!)
            : editor.value!.hasFormat(control.tag)
        );
      }
    }
  }

  // image paste listener
  editor.value!.addEventListener('pasteImage', async (e: Event) => {
    const clipData = (e as CustomEvent<{ clipboardData: DataTransfer }>).detail.clipboardData;
    if (!clipData) return;
    for (let index = 0; index < clipData.items.length; index++) {
      const element = clipData.items[index];
      if (!element.type.startsWith('image')) continue;
      const file = element.getAsFile();
      if (file) {
        const base64Image = await blobToBase64(file);
        editor.value!.insertImage(base64Image, { style: 'max-width: 600px' });
      } else {
        element.getAsString((base64Image) => {
          editor.value!.insertImage(base64Image, { style: 'max-width: 600px' });
        });
      }
      return;
    }
  });

  // Squire emits input for both typing and toolbar-driven document changes.
  editor.value.addEventListener('input', () => {
    if (!props.disabled) emit('change');
    const range = editor.value?.getSelection();
    const node = range?.startContainer;
    const element = node instanceof Element ? node : node?.parentElement;
    selectedTable.value = element?.closest('table') ?? null;
    updateTableHandle(selectedTable.value);
  });

  // rerender listener (updates currentValueByTag like italic, font, ...)
  editor.value!.addEventListener('pathChange', (e: Event) => {
    updateCurrentValues();
  });
  updateCurrentValues();

  // set initial selection
  editor.value!.moveCursorToStart();
  if (props.focusOnLoad) {
    editor.value!.focus();
  }

  if (props.disabled) {
    editorRef.value?.setAttribute('contenteditable', 'false');
  }
});

onUnmounted(() => {
  editor.value?.destroy();
});

const emit = defineEmits<{ focusout: []; change: [] }>();

defineExpose({
  getHTML: () => editor.value?.getHTML() ?? '',
  setHTML: (html: string) => {
    editor.value?.setHTML(html);
  },
  /**
   * Insert HTML at the current caret (or at the end if nothing is focused).
   * Used by text-block / snippet pickers (e.g. F5 in the full editor modal).
   */
  insertHTML: (html: string) => {
    if (props.disabled || !html || !editor.value) return;
    editor.value.focus();
    editor.value.insertHTML(html);
  },
  /** `atEnd` puts the caret behind the text, also over an older selection (BLP/nx#3318). */
  focus: (options?: { atEnd?: boolean }) => {
    editor.value?.focus();
    if (options?.atEnd === true) editor.value?.moveCursorToEnd();
  },
  getSelection: () => {
    return editor.value?.getSelection();
  },
  inputElement: editorRef,
});
</script>

<template>
  <div :class="['wrapper', { internalScroll }]">
    <component :is="'style'" v-if="tableAnchorCSS">{{ tableAnchorCSS }}</component>
    <div v-if="props.showActionBar" class="controls" role="toolbar" :aria-label="label">
      <template v-if="editor">
        <template v-for="(group, i) in controls" :key="i">
          <div class="group">
            <template v-for="control in group" :key="control.tag">
              <NxTooltip
                v-if="control.type === 'button'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <NxButton
                  width="icon-only"
                  variant="ghost"
                  :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @click="
                    () => {
                      callWithRef((asRef) => control.action(editor!, control.tag, asRef));
                      editor!.focus();
                    }
                  "
                >
                  <component :is="control.icon" filled></component>
                </NxButton>
              </NxTooltip>
              <NxTooltip
                v-else-if="control.type === 'toggle'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <NxButton
                  width="icon-only"
                  variant="ghost"
                  :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  :class="{ active: currentValueByTag.get(control.tag) as boolean }"
                  :aria-pressed="currentValueByTag.get(control.tag) as boolean"
                  @click="
                    () => {
                      callWithRef((asRef) => control.action(editor!, control.tag, asRef));
                      editor!.focus();
                    }
                  "
                >
                  <component :is="control.icon" filled></component>
                </NxButton>
              </NxTooltip>
              <!-- mousedown.prevent so the focus/selection is kept inside the editor -->
              <NxSelectInput
                v-else-if="control.type === 'select'"
                :options="control.values"
                :aria-label="i18nInjection.t(`common.editor.${control.tag}`)"
                :model-value="[currentValueByTag.get(control.tag) as string]"
                placeholder="<Default>"
                :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                :wrapper-style="{
                  width: `${Math.max(5, '<Default>'.length, ...control.values.map((v) => v.length + 5))}ch`,
                }"
                :style="{ height: 'var(--nxt-control-size-md)' }"
                @mousedown.prevent
                @update:model-value="
                  (v) => {
                    // after (i.e. setTimeout) is needed as NxSelectInput gets focus after model-value update.
                    // So without after the focus changes like this: editor > select > editor > select > editor
                    // But as each editor focus cleans up empty objects the newly created span with e.g. font-size
                    // 64px gets pruned. If we use after the focus is like: editor > select > editor.
                    after(() => {
                      v.length > 0 && v[0] !== null
                        ? control.action(v[0], editor!, control.tag)
                        : control.action(control.default, editor!, control.tag);
                    }, 1);
                  }
                "
              ></NxSelectInput>
              <NxTooltip
                v-if="control.type === 'add-link'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <AddLink
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @add-link="
                    (link) => {
                      control.action(link, editor!);
                    }
                  "
                  @close="() => editor!.focus()"
                >
                  <NxButton
                    width="icon-only"
                    variant="ghost"
                    :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                    :disabled="
                      props.disabled || (control.disableIfImageSelected && !!selectedImage)
                    "
                  >
                    <component :is="control.icon" filled></component>
                  </NxButton>
                </AddLink>
              </NxTooltip>
              <NxTooltip
                v-if="control.type === 'add-table'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <AddTable
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @add-table="
                    (w, h) => {
                      selectedTable = control.action(w, h, editor!);
                    }
                  "
                  @close="() => editor!.focus()"
                >
                  <NxButton
                    width="icon-only"
                    variant="ghost"
                    :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                    :disabled="
                      props.disabled || (control.disableIfImageSelected && !!selectedImage)
                    "
                  >
                    <component :is="control.icon" filled></component>
                  </NxButton>
                </AddTable>
              </NxTooltip>
              <NxTooltip
                v-if="control.type === 'line-spacing'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <ChangeLineSpacing
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @select="
                    (lineSpacing) => {
                      control.action(lineSpacing, editor!);
                    }
                  "
                  @close="editor!.focus()"
                >
                  <NxButton
                    width="icon-only"
                    variant="ghost"
                    :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                    :disabled="
                      props.disabled || (control.disableIfImageSelected && !!selectedImage)
                    "
                  >
                    <component :is="control.icon" filled></component>
                  </NxButton>
                </ChangeLineSpacing>
              </NxTooltip>
              <NxTooltip
                v-if="control.type === 'color-picker'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <ColorPicker
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @select-color="
                    (color) => {
                      control.action(color, editor!);
                    }
                  "
                  @close="editor!.focus()"
                >
                  <NxButton
                    width="icon-only"
                    variant="ghost"
                    :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                    :disabled="
                      props.disabled || (control.disableIfImageSelected && !!selectedImage)
                    "
                  >
                    <component :is="control.icon" filled></component>
                  </NxButton>
                </ColorPicker>
              </NxTooltip>
              <NxTooltip
                v-if="control.type === 'add-image'"
                :text="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
              >
                <!-- use label as it will trigger the for-element i.e. the file-input -->
                <label
                  :for="`add-image-${uniqueId}`"
                  :style="{
                    cursor:
                      control.disableIfImageSelected && !!selectedImage ? 'not-allowed' : 'pointer',
                  }"
                >
                  <NxButton
                    width="icon-only"
                    variant="ghost"
                    :aria-label="i18nInjection.t(`common.editor.${editorI18nTag(control.tag)}`)"
                    style="pointer-events: none"
                    :disabled="
                      props.disabled || (control.disableIfImageSelected && !!selectedImage)
                    "
                  >
                    <component :is="control.icon" filled></component>
                  </NxButton>
                </label>
                <!-- hidden input -->
                <input
                  :id="`add-image-${uniqueId}`"
                  class="hidden-input"
                  type="file"
                  accept="image/*"
                  multiple
                  tabindex="-1"
                  :disabled="props.disabled || (control.disableIfImageSelected && !!selectedImage)"
                  @change="
                    (event) => {
                      const files = (event.target as HTMLInputElement).files;
                      if (files) control.action(files, editor!);
                      editor!.focus();
                    }
                  "
                />
              </NxTooltip>
            </template>
          </div>
        </template>
      </template>
    </div>
    <div class="editor-wrapper" :style="{ overflow: internalScroll ? 'auto' : undefined }">
      <div
        ref="editor-ref"
        class="editor"
        :data-nx-editor="uniqueId"
        role="textbox"
        aria-multiline="true"
        :aria-label="label"
        :aria-disabled="props.disabled || undefined"
        :contenteditable="!props.disabled"
        :data-hide-selection="hideSelection ? true : undefined"
        :style="{
          anchorName: anchorName,
          padding: internalScroll ? 'var(--nxt-space-md)' : undefined,
          height: internalScroll ? 'calc(100% - 2 * 1em)' : '100%',
        }"
        @keydown="onKeydown"
        @click="onClick"
        @mousedown="
          (e) => {
            if ((e.target as Element).tagName === 'IMG') {
              hideSelection = true;
            } else {
              hideSelection = false;
            }
          }
        "
        @focusout="() => emit('focusout')"
      ></div>
      <!-- table handles -->
      <template v-if="selectedTable && !props.disabled">
        <div
          v-for="x in selectedTableColCount"
          :key="`delete-col-handle-${x}`"
          :style="{
            position: 'absolute',
            bottom: `anchor(--td-${x - 1}-${0}-${uniqueId} top)`,
            left: `anchor(--td-${x - 1}-${0}-${uniqueId} left)`,
            right: `anchor(--td-${x - 1}-${0}-${uniqueId} right)`,
          }"
          class="table-grip table-grip-col"
          @pointerdown.prevent="editor?.saveUndoState()"
          @click="
            () => {
              if (selectedTableColCount === 1) {
                selectedTable?.remove();
                selectedTable = null;
              } else {
                deleteCol(selectedTable!, x - 1);
                updateTableHandle(selectedTable);
              }
            }
          "
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="grip-delete">&times;</span>
        </div>
        <div
          v-for="y in selectedTableRowCount"
          :key="`delete-row-handle-${y}`"
          :style="{
            position: 'absolute',
            top: `anchor(--td-${0}-${y - 1}-${uniqueId} top)`,
            bottom: `anchor(--td-${0}-${y - 1}-${uniqueId} bottom)`,
            right: `anchor(--td-${0}-${y - 1}-${uniqueId} left)`,
          }"
          class="table-grip table-grip-row"
          @pointerdown.prevent="editor?.saveUndoState()"
          @click="
            () => {
              if (selectedTableRowCount === 1) {
                selectedTable?.remove();
                selectedTable = null;
              } else {
                deleteRow(selectedTable!, y - 1);
                updateTableHandle(selectedTable);
              }
            }
          "
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="grip-delete">&times;</span>
        </div>

        <div
          v-for="x in selectedTableColCount + 1"
          :key="`add-col-handle-${x}`"
          :style="{
            position: 'absolute',
            bottom: `anchor(--td-${x > selectedTableColCount ? x - 2 : x - 1}-${0}-${uniqueId} top)`,
            left:
              x > selectedTableColCount
                ? `anchor(--td-${x - 2}-${0}-${uniqueId} right)`
                : `anchor(--td-${x - 1}-${0}-${uniqueId} left)`,
          }"
          class="table-add-btn table-add-col"
          @pointerdown.prevent="editor?.saveUndoState()"
          @click="
            () => {
              insertCol(selectedTable!, x - 1);
              updateTableHandle(selectedTable);
            }
          "
        ></div>
        <div
          v-for="y in selectedTableRowCount + 1"
          :key="`add-row-handle-${y}`"
          :style="{
            position: 'absolute',
            top:
              y > selectedTableRowCount
                ? `anchor(--td-${0}-${y - 2}-${uniqueId} bottom)`
                : `anchor(--td-${0}-${y - 1}-${uniqueId} top)`,
            right: `anchor(--td-${0}-${y > selectedTableRowCount ? y - 2 : y - 1}-${uniqueId} left)`,
          }"
          class="table-add-btn table-add-row"
          @pointerdown.prevent="editor?.saveUndoState()"
          @click="
            () => {
              insertRow(selectedTable!, y - 1, selectedTableColCount);
              updateTableHandle(selectedTable);
            }
          "
        ></div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.wrapper {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;

  &.internalScroll {
    border: var(--nxt-hairline) solid var(--nxt-border);
  }
}

.controls {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-md);
  background-color: var(--nxt-brand-subtle-bg);
  z-index: var(--nxt-z-sticky);

  .group {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 0.125em;
    border-radius: var(--nxt-radius-sm);
    border: var(--nxt-hairline) solid transparent;

    .active {
      border: var(--nxt-hairline) solid var(--nxt-brand-border);
    }
  }
}

.editor-wrapper {
  flex-grow: 1;
  padding: 0;
  position: relative;
}

.editor {
  outline: none;
  color: var(--nxt-fg);
  font-size: 1em; /* em to grow with height */
  z-index: var(--nxt-z-raised);

  &[data-hide-selection='true']::selection {
    /* caret-color: transparent; */
    background-color: transparent;
  }
}

.hidden-input {
  pointer-events: none;
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
}

.table-grip {
  pointer-events: auto;
  position: absolute;
  background: var(--nxt-brand-subtle-bg);
  border-radius: var(--nxt-radius-sm);
  cursor: pointer;
  /* transition: background 0.1s ease-in-out; */
  display: flex;
  align-items: center;
  justify-content: center;

  &.table-grip-col {
    height: calc(var(--nxt-size-unit) * 0.75);
  }

  &.table-grip-row {
    width: calc(var(--nxt-size-unit) * 0.75);
  }

  &:hover {
    /* destructive affordance (delete row/col) */
    background: var(--nxt-danger-bg);

    .grip-delete {
      color: var(--nxt-danger-fg);
    }
  }

  .grip-delete {
    font-size: var(--nxt-font-size-xs);
    font-weight: var(--nxt-font-weight-semibold);
    color: transparent;
    line-height: 1;
  }
}

.table-add-btn {
  pointer-events: auto;
  width: calc(var(--nxt-size-unit) * 1.25);
  height: calc(var(--nxt-size-unit) * 1.25);
  cursor: pointer;
  z-index: var(--nxt-z-float);

  &.table-add-row {
    transform: translateY(-50%);
  }
  &.table-add-col {
    transform: translateX(-50%);
  }

  /* visible dot */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(var(--nxt-size-unit) * 0.25);
    height: calc(var(--nxt-size-unit) * 0.25);
    background: var(--nxt-fg-subtle);
    border-radius: 50%;
    transition: all var(--nxt-motion-fast) var(--nxt-ease-in-out);
  }

  /* Plus-Icon */
  &::after {
    content: '+';
    position: absolute;
    width: var(--nxt-icon-size-md);
    height: var(--nxt-icon-size-md);
    background: var(--nxt-brand-bg);
    color: var(--nxt-brand-fg);
    border-radius: 50%;
    font-size: var(--nxt-font-size-md);
    font-weight: var(--nxt-font-weight-regular);
    opacity: 0;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: var(--nxt-z-float);
    transition: opacity var(--nxt-motion-fast) var(--nxt-ease-in-out);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover::before {
    opacity: 0;
  }

  &:hover::after {
    opacity: 1;
  }
}
</style>
